// src/components/SensorPage/SensorPage.jsx - Sidebar Padronizado
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiMenu,
  FiSearch,
  FiBell,
  FiSettings,
  FiChevronRight,
  FiBarChart2,
  FiCpu,
  FiActivity,
  FiMessageSquare,
  FiThermometer,
  FiWind,
  FiTarget,
  FiHeart,
  FiZap,
  FiRefreshCw,
  FiClock,
  FiPlay,
  FiSliders,
  FiTrendingUp,
  FiMonitor,
  FiFileText,
  FiAlertCircle,
  FiLayers,
  FiGlobe,
  FiSun,
  FiMoon,
  FiHome,
  FiArrowLeft
} from 'react-icons/fi';
import StrategicData from './StrategicData';
import TechnicalData from './TechnicalData';
import Dashboard from './Dashboard';
import Insights from './Insights';
import { useSensorData } from './useSensorData';
import UserProfile from '../Globe/UserProfile';
import './SensorPage.css';
import '../Globe/UserProfile.css';

const SensorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('strategic');
  const [activeSubsection, setActiveSubsection] = useState('comfort');
  const [expandedSections, setExpandedSections] = useState(['strategic']);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  
  // Obter email do usu�rio logado
  const [userEmail, setUserEmail] = useState(() => {
    return localStorage.getItem('user_email') || sessionStorage.getItem('user_email') || '';
  });
  
  // Fun��o de logout
  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_email');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_email');
    navigate('/login');
  };

  const [timeRange, setTimeRange] = useState('24h');
  const { data, loading, error, refetch } = useSensorData(id || 'sensor-001', timeRange);
  
  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Navigation structure
  const navigationItems = [
    {
      id: 'strategic',
      label: 'Dados Estratégicos',
      icon: FiBarChart2,
      color: '#3b82f6',
      badge: null,
      subsections: [
        { id: 'comfort', label: 'Nível de Conforto', icon: FiThermometer },
        { id: 'airQuality', label: 'QAI', icon: FiWind },
        { id: 'cognition', label: 'Cognição', icon: FiTarget },
        { id: 'health', label: 'Saúde', icon: FiHeart },
        { id: 'efficiency', label: 'Eficiência HVAC', icon: FiZap },
        { id: 'ventilation', label: 'Renovação de Ar', icon: FiRefreshCw }
      ]
    },
    {
      id: 'technical',
      label: 'Dados Técnicos',
      icon: FiCpu,
      color: '#8b5cf6',
      badge: null,
      subsections: [
        { id: 'runtime', label: 'Tempo HVAC', icon: FiClock },
        { id: 'optimus', label: 'Optimum Start/Stop', icon: FiPlay },
        { id: 'setpoint', label: 'Setpoint Dinâmico', icon: FiSliders }
      ]
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: FiActivity,
      color: '#10b981',
      badge: 'Tempo Real',
      subsections: [
        { id: 'instant', label: 'Dados Instantâneos', icon: FiMonitor },
        { id: 'historical', label: 'Histórico de Leituras', icon: FiTrendingUp }
      ]
    },
    {
      id: 'insights',
      label: 'Insights',
      icon: FiMessageSquare,
      color: '#f59e0b',
      badge: '3 novos',
      subsections: [
        { id: 'ai', label: 'Sugestões IA', icon: FiFileText },
        { id: 'alerts', label: 'Alertas', icon: FiAlertCircle },
        { id: 'patterns', label: 'Padrões', icon: FiLayers }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleNavigation = (sectionId, subsectionId) => {
    setActiveSection(sectionId);
    setActiveSubsection(subsectionId);
    if (!expandedSections.includes(sectionId)) {
      setExpandedSections([...expandedSections, sectionId]);
    }
    setMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <div className="sensor-page-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Carregando dados do sensor...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="sensor-page-error">
        <FiAlertCircle size={48} />
        <h2>Erro ao carregar dados</h2>
        <p>Não foi possível conectar ao sensor</p>
        <button className="error-button" onClick={() => navigate('/globe')}>
          Voltar ao Mapa
        </button>
      </div>
    );
  }

  return (
    <div className="sensor-page-container">
      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            className="mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>
      
          {/* Sidebar */}
          <aside className={`sensor-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Sidebar Header com Logo Uber */}
          <div className="sidebar-header-brand">
            <div className="sidebar-brand-content">
              <div className="brand-icon">
                <img 
                  src="/assets/images/uber-logo.png" 
                  alt="Uber" 
                  className="sidebar-uber-logo"
                />
              </div>
              {!sidebarCollapsed && (
                <div className="sidebar-brand-text">
                  <h3>STATS Protocol</h3>
                  <span>Environmental Intelligence</span>
                </div>
              )}
            </div>
          </div>


        {/* Back to Map Button */}
        <div className="sidebar-back-section">
          <button
            className="nav-item back-to-map"
            onClick={() => navigate('/globe')}
          >
            <div className="nav-item-content">
              <div className="nav-item-icon" style={{ color: '#6366f1' }}>
                <FiArrowLeft size={20} />
              </div>
              {!sidebarCollapsed && (
                <span className="nav-item-label">Voltar ao Mapa</span>
              )}
            </div>
          </button>
        </div>

        <nav className="sidebar-nav">
          {navigationItems.map(section => {
            const SectionIcon = section.icon;
            const isExpanded = expandedSections.includes(section.id);
            const isActive = activeSection === section.id;

            return (
              <div key={section.id} className="nav-group">
                <button
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    toggleSection(section.id);
                    if (!isExpanded && section.subsections.length > 0) {
                      handleNavigation(section.id, section.subsections[0].id);
                    }
                  }}
                  style={isActive ? { '--accent-color': section.color } : {}}
                >
                  <div className="nav-item-content">
                    <div className="nav-item-icon" style={{ color: section.color }}>
                      <SectionIcon size={20} />
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <span className="nav-item-label">{section.label}</span>
                        {section.badge && (
                          <span className="nav-item-badge" style={{ background: section.color }}>
                            {section.badge}
                          </span>
                        )}
                        {section.subsections.length > 0 && (
                          <FiChevronRight className={`nav-item-arrow ${isExpanded ? 'expanded' : ''}`} />
                        )}
                      </>
                    )}
                  </div>
                </button>

                {!sidebarCollapsed && (
                  <AnimatePresence>
                    {isExpanded && section.subsections.length > 0 && (
                      <motion.div
                        className="nav-submenu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {section.subsections.map(subsection => {
                          const SubIcon = subsection.icon;
                          const isSubActive = activeSection === section.id && activeSubsection === subsection.id;

                          return (
                            <button
                              key={subsection.id}
                              className={`nav-subitem ${isSubActive ? 'active' : ''}`}
                              onClick={() => handleNavigation(section.id, subsection.id)}
                            >
                              <SubIcon size={16} />
                              <span>{subsection.label}</span>
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Header */}
        <header className="top-header">
          <div className="header-left">
            <button 
              className="menu-toggle"
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setMobileMenuOpen(!mobileMenuOpen);
                } else {
                  setSidebarCollapsed(!sidebarCollapsed);
                }
              }}
            >
              <FiMenu />
            </button>
            <button 
              className="back-to-map-header"
              onClick={() => navigate('/globe')}
              title="Voltar ao Mapa"
            >
              <FiArrowLeft size={18} />
              <FiGlobe size={18} />
            </button>
            <div className="header-info">
              <h1>{data.location.name}</h1>
              <p>{data.location.address}</p>
            </div>
          </div>

          <div className="header-right">
            <div className="header-search">
              <FiSearch />
              <input type="text" placeholder="Buscar dados..." />
            </div>

            <div className="header-actions">
              <button className="action-button" onClick={toggleTheme}>
                {theme === 'light' ? <FiMoon /> : <FiSun />}
              </button>
              <button className="action-button notification">
                <FiBell />
                <span className="notification-dot"></span>
              </button>
              <button className="action-button">
                <FiSettings />
              </button>
            </div>

            {/* Componente UserProfile com dropdown */}
            {userEmail && (
              <UserProfile 
                userEmail={userEmail} 
                onLogout={handleLogout}
              />
            )}
          </div>
        </header>

        {/* Content Area */}
        <main className="content-area">
          {activeSection === 'strategic' && (
            <StrategicData 
              data={data} 
              activeSubsection={activeSubsection}
              timeRange={timeRange}
              onTimeRangeChange={handleTimeRangeChange}
            />
          )}
          {activeSection === 'technical' && (
            <TechnicalData 
              data={data} 
              activeSubsection={activeSubsection}
            />
          )}
          {activeSection === 'dashboard' && (
            <Dashboard 
              data={data} 
              activeSubsection={activeSubsection}
              onTimeRangeChange={handleTimeRangeChange}
              timeRange={timeRange}
            />
          )}
          {activeSection === 'insights' && (
            <Insights 
              data={data} 
              activeSubsection={activeSubsection}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default SensorPage;
